import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BOTTOM_INDENTS, COLORS, TOP_INDENTS } from '@constants';
import { Divider, Icon, Typography } from '@ui';
import { Flex } from 'antd';
import { StyledCloseButton, StyledMask, StyledWrapper } from './styled';

const AiSetup = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [fetchingCounter, setFetchingCounter] = useState(0);
    const [parsingCounter, setParsingCounter] = useState(0);
    const [normalizingCounter, setNormalizingCounter] = useState(0);
    const [extractingCounter, setExtractingCounter] = useState(0);
    const [preparingCounter, setPreparingCounter] = useState(0);

    const removeAiParam = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('ai');
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        document.querySelector('#root').style.overflow = 'hidden';

        const animate = (setter) =>
            new Promise((resolve) => {
                let counter = 0;
                const duration = 1000 + Math.random() * 2000;
                const stepInterval = duration / 100;
                const id = setInterval(() => {
                    counter += 1;
                    setter(counter);
                    if (counter >= 100) {
                        clearInterval(id);
                        resolve();
                    }
                }, stepInterval);
            });
        (async () => {
            await animate(setFetchingCounter);
            await animate(setParsingCounter);
            await animate(setNormalizingCounter);
            await animate(setExtractingCounter);
            await animate(setPreparingCounter);
            setTimeout(removeAiParam, 200);
        })();

        return () => {
            document.querySelector('#root').style.overflow = 'scroll';
        };
    }, [searchParams, setSearchParams]);

    return (
        <StyledMask>
            <StyledWrapper>
                <StyledCloseButton data-e2e='ai-setup_close_button' onClick={removeAiParam}>
                    <Icon.CloseIcon />
                </StyledCloseButton>
                <div data-e2e='ai-setup_mask'>
                    <Typography.Title align='center' level={2} color={COLORS.$gray100Black}>
                        Setting up your ads...
                    </Typography.Title>
                    <Divider margin='24px 0' />
                    <Flex vertical>
                        <Flex align='center' justify='space-between' style={BOTTOM_INDENTS.M}>
                            <Flex gap={24} align='center'>
                                <Icon.PackageIcon />
                                <Typography.Text type='descriptor1'>Fetching raw product data...</Typography.Text>
                            </Flex>
                            <Typography.Text type='badgeLarge' color={COLORS.$pink}>
                                {fetchingCounter ? (fetchingCounter >= 100 ? 'Done!' : `${fetchingCounter}%`) : ''}
                            </Typography.Text>
                        </Flex>
                        <Flex align='center' justify='space-between' style={BOTTOM_INDENTS.M}>
                            <Flex gap={24} align='center'>
                                <Icon.PackageSearchIcon />
                                <Typography.Text type='descriptor1'>
                                    Parsing and preprocessing entries...
                                </Typography.Text>
                            </Flex>
                            <Typography.Text type='badgeLarge' color={COLORS.$pink}>
                                {parsingCounter ? (parsingCounter >= 100 ? 'Done!' : `${parsingCounter}%`) : ''}
                            </Typography.Text>
                        </Flex>
                        <Flex align='center' justify='space-between' style={BOTTOM_INDENTS.M}>
                            <Flex gap={24} align='center'>
                                <Icon size='32px' fill={COLORS.$gray60Black} component={() => <Icon.CarbonIcon />} />
                                <Typography.Text type='descriptor1'>
                                    Normalizing attributes and categories…
                                </Typography.Text>
                            </Flex>
                            <Typography.Text type='badgeLarge' color={COLORS.$pink}>
                                {normalizingCounter
                                    ? normalizingCounter >= 100
                                        ? 'Done!'
                                        : `${normalizingCounter}%`
                                    : ''}
                            </Typography.Text>
                        </Flex>
                        <Flex align='center' justify='space-between' style={BOTTOM_INDENTS.M}>
                            <Flex gap={24} align='center'>
                                <Icon.LayersLightIcon />
                                <Typography.Text type='descriptor1'>
                                    Extracting features with AI models...
                                </Typography.Text>
                            </Flex>
                            <Typography.Text type='badgeLarge' color={COLORS.$pink}>
                                {extractingCounter
                                    ? extractingCounter >= 100
                                        ? 'Done!'
                                        : `${extractingCounter}%`
                                    : ''}
                            </Typography.Text>
                        </Flex>
                        <Flex align='center' justify='space-between' style={BOTTOM_INDENTS.M}>
                            <Flex gap={24} align='center'>
                                <Icon size='32px' fill={COLORS.$gray60Black} component={() => <Icon.NimbusIcon />} />
                                <Typography.Text type='descriptor1'>
                                    Preparing dataset for deployment...
                                </Typography.Text>
                            </Flex>
                            <Typography.Text type='badgeLarge' color={COLORS.$pink}>
                                {preparingCounter ? (preparingCounter >= 100 ? 'Done!' : `${preparingCounter}%`) : ''}
                            </Typography.Text>
                        </Flex>
                    </Flex>
                    <Typography.Text type='badgeMedium' color={COLORS.$pink} align='center' style={TOP_INDENTS.XL}>
                        Please wait...
                    </Typography.Text>
                </div>
            </StyledWrapper>
        </StyledMask>
    );
};

export default AiSetup;
